import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useStore } from "@/context/StoreContext";
import { Marquee } from "@/components/Marquee";
import { Reveal } from "@/components/Reveal";
import { ProductCard } from "@/components/ProductCard";
import { Testimonials } from "@/components/Testimonials";
import { FaqAccordion } from "@/components/FaqAccordion";
import { Button } from "@/components/ui/button";
import { ArrowRight, Download, Infinity as InfinityIcon, Zap, RefreshCw, Users, Check } from "lucide-react";

const features = [
  { icon: Download, title: "Instant Download", text: "Get your guides the second you pay. No waiting, no shipping." },
  { icon: InfinityIcon, title: "Lifetime Access", text: "Buy once, keep forever. Re-download anytime you need it." },
  { icon: Zap, title: "Actionable Information", text: "Real systems and steps — not vague theory or filler." },
  { icon: RefreshCw, title: "Regular Updates", text: "Guides stay current. New versions, always free." },
  { icon: Users, title: "Thousands of Happy Customers", text: "Join a community that took action and saw results." },
];

export default function Home() {
  const { products, bundle, openCheckout } = useStore();
  const navigate = useNavigate();

  return (
    <div>
      {/* HERO */}
      <section className="relative mx-auto max-w-7xl px-5 pt-16 lg:px-8 lg:pt-24" data-testid="hero-section">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-8">
          <div>
            <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground">Premium digital guides</motion.p>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.05 }}
              className="mt-5 font-display text-5xl font-extrabold leading-[0.95] tracking-tighter sm:text-6xl lg:text-7xl">
              Learn Skills<br />That Change<br />Your Life.
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.15 }}
              className="mt-7 max-w-md text-base leading-relaxed text-muted-foreground">
              Premium digital guides designed to help you build muscle, dress with confidence and attract better relationships — the skills that actually change your life.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.25 }}
              className="mt-9 flex flex-wrap items-center gap-3">
              <Button data-testid="hero-shop-btn" onClick={() => navigate("/shop")}
                className="group rounded-none h-12 bg-primary px-7 text-primary-foreground hover:bg-primary/90">
                Shop PDFs <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button data-testid="hero-bundle-btn" variant="outline" onClick={() => navigate("/bundle")}
                className="rounded-none h-12 border-black/20 px-7 hover:bg-muted">View Bundle</Button>
            </motion.div>
            <div className="mt-8 flex items-center gap-2 text-xs text-muted-foreground">
              <Download className="h-3.5 w-3.5" /> Instant Download · Lifetime Access · Secure Stripe Checkout
            </div>
          </div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative">
            <div className="relative overflow-hidden border border-black/10 bg-muted">
              <img src={bundle?.mockup_image || "https://images.unsplash.com/photo-1560340199-2e81307bb313?crop=entropy&cs=srgb&fm=jpg&q=85"}
                alt="Premium PDF guides" className="aspect-[4/5] w-full object-cover grayscale" />
              <div className="absolute bottom-4 left-4 bg-white/90 px-4 py-2">
                <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{products.length || 3} Guides · 1 System</p>
                <p className="font-display text-lg tracking-tight">The Complete Bundle</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <div className="mt-20"><Marquee /></div>

      {/* CATEGORIES */}
      <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32" data-testid="categories-section">
        <Reveal>
          <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
            <div>
              <p className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground">The Blueprints</p>
              <h2 className="mt-3 max-w-2xl font-display text-4xl tracking-tight sm:text-5xl">Four guides. Every area of your life.</h2>
            </div>
            <Link to="/shop" className="text-sm underline underline-offset-4 hover:no-underline" data-testid="categories-viewall">View all guides</Link>
          </div>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
        </div>
      </section>

      {/* FEATURES */}
      <section className="border-y border-black/10 bg-muted" data-testid="features-section">
        <div className="mx-auto max-w-7xl px-5 py-24 lg:px-8">
          <Reveal><h2 className="max-w-xl font-display text-4xl tracking-tight sm:text-5xl">Built for people who take action.</h2></Reveal>
          <div className="mt-14 grid grid-cols-1 gap-px border border-black/10 bg-black/10 sm:grid-cols-2 lg:grid-cols-5">
            {features.map((f, i) => (
              <Reveal key={i} delay={i * 0.06} className="bg-muted">
                <div className="flex h-full flex-col p-7">
                  <f.icon className="h-6 w-6" />
                  <p className="mt-6 font-display text-lg tracking-tight">{f.title}</p>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BUNDLE TEASER */}
      {bundle && (
        <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32" data-testid="bundle-teaser">
          <div className="relative grid grid-cols-1 items-center gap-10 border border-black/10 bg-[#0a0a0a] p-8 text-white lg:grid-cols-2 lg:p-16">
            <div className="absolute right-0 top-0 bg-white px-4 py-1.5 text-[10px] uppercase tracking-[0.2em] text-foreground" data-testid="bundle-ribbon">Best Value</div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.25em] text-white/50">The Complete Blueprint Bundle</p>
              <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight sm:text-5xl">Get everything.<br />Save ${(bundle.compare_at_price - bundle.price).toFixed(0)}.</h2>
              <ul className="mt-8 space-y-3">
                {(bundle.whats_included || []).map((g) => (
                  <li key={g} className="flex items-center gap-3 text-sm text-white/80"><Check className="h-4 w-4" /> {g}</li>
                ))}
              </ul>
              <div className="mt-8 flex items-end gap-3">
                <span className="font-display text-4xl">${bundle.price?.toFixed(2)}</span>
                <span className="mb-1 text-lg text-white/40 line-through">${bundle.compare_at_price?.toFixed(2)}</span>
                <span className="mb-1 bg-white px-2 py-0.5 text-[11px] font-medium text-foreground">Save ${(bundle.compare_at_price - bundle.price).toFixed(0)}</span>
              </div>
              <Button data-testid="bundle-teaser-cta" onClick={() => openCheckout({ item_type: "bundle", slug: "complete-bundle", title: bundle.title, price: bundle.price })}
                className="mt-8 rounded-none h-12 bg-white px-8 text-foreground hover:bg-white/90">Get Everything</Button>
            </div>
            <div className="overflow-hidden border border-white/10">
              <img src={bundle.mockup_image} alt={bundle.title} className="aspect-[4/3] w-full object-cover grayscale" />
            </div>
          </div>
        </section>
      )}

      <Testimonials />

      {/* ABOUT TEASER */}
      <section className="border-y border-black/10 bg-muted" data-testid="about-teaser">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-5 py-24 lg:grid-cols-2 lg:px-8 lg:py-32">
          <Reveal><h2 className="font-display text-4xl font-extrabold tracking-tight sm:text-5xl">Helping people improve every area of their life.</h2></Reveal>
          <Reveal delay={0.1}>
            <p className="text-base leading-relaxed text-muted-foreground">Our guides are designed to provide practical systems that help people improve their fitness, style and relationships. No fluff, no hype — just proven, actionable frameworks you can start using today.</p>
            <Link to="/about" className="mt-6 inline-flex items-center gap-2 text-sm underline underline-offset-4 hover:no-underline" data-testid="about-teaser-link">Read our story <ArrowRight className="h-4 w-4" /></Link>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-5 py-24 lg:py-32" data-testid="home-faq">
        <Reveal><h2 className="font-display text-4xl tracking-tight sm:text-5xl">Questions? Answered.</h2></Reveal>
        <div className="mt-10"><FaqAccordion /></div>
      </section>

      {/* FINAL CTA */}
      <section className="mx-auto max-w-7xl px-5 pb-24 lg:px-8 lg:pb-32">
        <div className="border border-black/10 p-10 text-center lg:p-20">
          <h2 className="mx-auto max-w-2xl font-display text-4xl font-extrabold tracking-tight sm:text-5xl">Ready to upgrade your life?</h2>
          <p className="mx-auto mt-4 max-w-md text-muted-foreground">Start with one guide or get the complete bundle and transform everything.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button data-testid="final-cta-shop" onClick={() => navigate("/shop")} className="rounded-none h-12 bg-primary px-8 text-primary-foreground hover:bg-primary/90">Shop PDFs</Button>
            <Button data-testid="final-cta-bundle" variant="outline" onClick={() => navigate("/bundle")} className="rounded-none h-12 border-black/20 px-8">View Bundle</Button>
          </div>
        </div>
      </section>
    </div>
  );
}
